import { COLORS } from '../constants.js';

// Helper function to check if a segment is projected
const isProjectedSegment = (ctx, projectedFlags) => {
  const currentIndex = ctx.p0DataIndex;
  const nextIndex = ctx.p1DataIndex;
  return projectedFlags[currentIndex] || projectedFlags[nextIndex];
};

// Helper function to create segment configuration for projected styling
const createSegmentConfig = (projectedFlags, solidColor, dashedColor) => ({
  borderColor: (ctx) => {
    return isProjectedSegment(ctx, projectedFlags) ? dashedColor : solidColor;
  },
  borderDash: (ctx) => {
    return isProjectedSegment(ctx, projectedFlags) ? [3, 2] : undefined;
  },
});

const baseLineChartConfig = {
  type: 'line',
  tension: 0.4,
  borderWidth: 1,
  pointRadius: 0,
};

const clipConfig = {
  left: false,
  top: 0,
  right: false,
  bottom: 0,
};

const noHoverConfig = {
  pointHoverBorderWidth: 0,
  pointHoverRadius: 0,
  pointHitRadius: 0,
};

export const createMeanDataset = (dataMean, projectedFlags = []) => ({
  ...baseLineChartConfig,
  label: 'Mean Estimate',
  data: dataMean,
  clip: clipConfig,
  borderColor: COLORS.primaryLine,
  pointHoverBackgroundColor: COLORS.indicatorHover,
  pointHoverBorderColor: COLORS.primaryLine,
  pointHoverBorderWidth: 1,
  pointHoverRadius: 4,
  pointHitRadius: 10,
  order: 1,
  segment: createSegmentConfig(
    projectedFlags,
    COLORS.primaryLine,
    COLORS.projectedMeanLine,
  ),
});

export const createP5Dataset = (dataP5, projectedFlags = []) => ({
  ...baseLineChartConfig,
  ...noHoverConfig,
  label: '5th Percentile',
  data: dataP5,
  clip: { ...clipConfig, left: -0.5 },
  backgroundColor: COLORS.primaryAreaBg,
  borderColor: COLORS.secondaryLine,
  order: 3,
  segment: createSegmentConfig(
    projectedFlags,
    COLORS.secondaryLine,
    COLORS.projectedSecondaryLine,
  ),
});

export const createP95Dataset = (dataP95, projectedFlags = []) => ({
  ...baseLineChartConfig,
  ...noHoverConfig,
  label: '95th Percentile',
  data: dataP95,
  clip: { ...clipConfig, left: -0.5 },
  backgroundColor: COLORS.primaryAreaBg,
  borderColor: COLORS.secondaryLine,
  fill: '-1',
  order: 3,
  segment: createSegmentConfig(
    projectedFlags,
    COLORS.secondaryLine,
    COLORS.projectedSecondaryLine,
  ),
});

// Transparent bars for hover interactions
export const createIndicatorDataset = (dataMean) => ({
  label: 'Indicator',
  data: dataMean,
  type: 'bar',
  clip: { ...clipConfig, left: 0.5 },
  borderSkipped: false,
  hidden: false,
  showLine: true,
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  borderWidth: 0,
  barThickness: 0.5,
  hoverBackgroundColor: COLORS.indicatorHover,
  hoverBorderColor: COLORS.indicatorHover,
  hoverBorderWidth: 1,
  hoverBorderRadius: 0,
  order: 2,
});

export const createDatasets = ({
  dataMean,
  dataP5,
  dataP95,
  projectedFlags = [],
}) => [
  createMeanDataset(dataMean, projectedFlags),
  createP5Dataset(dataP5, projectedFlags),
  createP95Dataset(dataP95, projectedFlags),
  createIndicatorDataset(dataMean),
];
